import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import "./styles/Cart.css";
import "../styles/global.css";
import "../styles/variables.css";
import { requireLogin } from "../utils/authRedirect";

/* ── Cart expiry (5 minutes) ────────────────────────────────────────────
   The cart itself lives on the backend (keyed by device_id) with no
   built-in expiry. We track a client-side expiry timestamp in
   localStorage: the moment the cart is first seen with items, a 5-minute
   countdown starts. If that time runs out — whether the user is sitting
   on the page or comes back later — the cart is cleared, both visually
   and on the server (there's no single "clear cart" endpoint, so we
   loop the existing removeFromCart call over every item). */
const CART_TTL_MS = 60 * 60 * 1000; // 1 HOUR
const CART_EXPIRY_KEY = "cart_expiry_at";

const formatCountdown = (ms) => {
  if (ms == null) return "";
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartAmount, setCartAmount] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [deliveryCharges, setDeliveryCharges] = useState(0);

  // ── Cart expiry state ──
  const [expiryAt, setExpiryAt] = useState(null);   // epoch ms when cart should clear
  const [countdown, setCountdown] = useState(null);  // ms remaining, for display
  const cartItemsRef = useRef(cartItems);            // always-fresh items for the interval closure
  const isExpiringRef = useRef(false);               // guards against double-clearing

  useEffect(() => { cartItemsRef.current = cartItems; }, [cartItems]);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }

  const finalAmount = cartAmount + deliveryCharges;

  /* Keeps the 5-minute timer in sync with whatever the cart currently
     looks like: starts it the first time items appear, leaves it alone
     if it's already running, and clears it once the cart is empty so
     the next items added get a fresh 5 minutes. */
  const syncExpiryForItems = (items) => {
    if (items.length > 0) {
      const stored = Number(localStorage.getItem(CART_EXPIRY_KEY)) || null;
      if (!stored) {
        const newExpiry = Date.now() + CART_TTL_MS;
        localStorage.setItem(CART_EXPIRY_KEY, String(newExpiry));
        setExpiryAt(newExpiry);
      } else {
        setExpiryAt(stored);
      }
    } else {
      localStorage.removeItem(CART_EXPIRY_KEY);
      setExpiryAt(null);
    }
  };

  /* Removes every item from the server-side cart (no bulk "clear cart"
     endpoint exists, so this loops the existing removeFromCart call),
     then resets everything on screen. */
  const clearCartOnServer = async (items) => {
    if (!items || items.length === 0) return;
    const device_id = localStorage.getItem("device_id");
    await Promise.all(
      items.map((item) =>
        fetch(`${API_BASE_URL}api/removeFromCart`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ device_id, id: item.id }),
        }).catch(() => {})
      )
    );
  };

  const handleCartExpired = async () => {
    if (isExpiringRef.current) return;
    isExpiringRef.current = true;

    const itemsToClear = cartItemsRef.current;
    localStorage.removeItem(CART_EXPIRY_KEY);
    setExpiryAt(null);
    setCountdown(null);
    setCartItems([]);
    setCartCount(0);
    setCartAmount(0);
    setQuantities({});
    const el = document.querySelector(".cartcount");
    if (el) el.innerText = 0;

    await clearCartOnServer(itemsToClear);
    toast.info("Your cart expired after 5 minutes and has been cleared.");
    isExpiringRef.current = false;
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
      const data = await res.json();
      if (data.status) setDeliveryCharges(Number(data.data.delivery_charges) || 0);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const handlePlus = (id, currentQty) => {
    const newQty = (quantities[id] ?? currentQty) + 1;
    setQuantities((prev) => ({ ...prev, [id]: newQty }));
    updateQuantity(id, newQty);
  };

  const handleMinus = (id, currentQty) => {
    const newQty = Math.max((quantities[id] ?? currentQty) - 1, 1);
    setQuantities((prev) => ({ ...prev, [id]: newQty }));
    updateQuantity(id, newQty);
  };

  const handleCheckout = () => {
    if (!cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty. Please add items before proceeding.");
      return;
    }
    if (!requireLogin(navigate, { pathname: "/checkout", search: "" }, { message: "Please login to continue to checkout" })) return;
    navigate("/checkout");
  };

  const getCart = () => {
    fetch(`${API_BASE_URL}api/getCart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: localStorage.getItem("device_id") }),
    })
      .then((res) => res.json())
      .then((json) => {
        const items = Object.values(json.cart);

        // If we were away long enough that the 5-minute window already
        // lapsed, clear the cart now instead of showing stale items.
        const storedExpiry = Number(localStorage.getItem(CART_EXPIRY_KEY)) || null;
        if (items.length > 0 && storedExpiry && Date.now() >= storedExpiry) {
          cartItemsRef.current = items; // so handleCartExpired clears the right items
          handleCartExpired();
          return;
        }

        syncExpiryForItems(items);
        setCartCount(json.count);
        setCartItems(items);
        const totalAmount = items.reduce((sum, item) => {
          const itemprice = item.dprice
            ? item.price - (item.price * item.dprice) / 100
            : item.price;
          return sum + itemprice * item.qty;
        }, 0);
        setCartAmount(totalAmount);
      })
      .catch((err) => console.error(err));
  };

  const removeFromCart = (id) => {
    fetch(`${API_BASE_URL}api/removeFromCart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: localStorage.getItem("device_id"), id }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.status) {
          setCartCount(json.count);
          const el = document.querySelector(".cartcount");
          if (el) el.innerText = json.count;
          const items = Object.values(json.cart);
          syncExpiryForItems(items);
          setCartItems(items);
          const totalAmount = items.reduce((sum, item) => {
            const itemprice = item.dprice
              ? item.price - (item.price * item.dprice) / 100
              : item.price;
            return sum + itemprice * item.qty;
          }, 0);
          setCartAmount(totalAmount);
          toast.success("Removed from cart");
        }
      })
      .catch((err) => console.error(err));
  };

  const updateQuantity = async (id, qty) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/updateCartQty`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: localStorage.getItem("device_id"), id, qty }),
      });
      const json = await res.json();
      setCartCount(json.count);
      const items = Object.values(json.cart);
      syncExpiryForItems(items);
      setCartItems(items);
      const totalAmount = items.reduce((sum, item) => {
        const itemprice = item.dprice
          ? item.price - (item.price * item.dprice) / 100
          : item.price;
        return sum + itemprice * item.qty;
      }, 0);
      setCartAmount(totalAmount);
      const el = document.querySelector(".cartcount");
      if (el) el.innerText = json.count;
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    getCart();
    fetchSettings();
  }, []);

  // Live 5-minute countdown — ticks every second while the cart has
  // items, and auto-clears the cart the moment time runs out (no need
  // to refresh or leave the page for it to take effect).
  useEffect(() => {
    if (!expiryAt) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const remaining = expiryAt - Date.now();
      if (remaining <= 0) {
        handleCartExpired();
      } else {
        setCountdown(remaining);
      }
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiryAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Header />

      <div className="cart-page-wrapper">
        {/* Breadcrumb */}
        <section className="cl-breadcrumb-bar py-xl-3 py-3">
          <div className="container">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"> <Link to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Cart</li>
              </ol>
            </nav>
          </div>
        </section>

        {/* Main Cart Section */}
        <section className="cart-main-section">
          <div className="container">
            <div className="cart-page-header">
              <h1 className="cart-page-title">Your Cart</h1>
              <span className="cart-item-badge cartcount">{cartCount}</span>
              {countdown != null && cartItems.length > 0 && (
                <span
                  style={{
                    marginLeft: "12px",
                    fontSize: "0.85rem",
                    color: countdown <= 60000 ? "#dc2626" : "#6b7280",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  title="Your cart will automatically clear when this reaches 0:00"
                >
                  <i className="fa-regular fa-clock"></i>
                  Cart expires in {formatCountdown(countdown)}
                </span>
              )}
            </div>

            <div className="row g-4">
              {/* Left Column — Cart Items */}
              <div className="col-12 col-xl-8">
                <div className="cart-items-panel">
                  <div className="cart-panel-head">
                    <span className="cart-panel-label">Items</span>
                    <span className="cart-count-pill">
                      {cartCount} {cartCount === 1 ? "item" : "items"}
                    </span>
                  </div>

                  {cartItems.length > 0 ? (
                    cartItems.map((product) => {
                      const qty = quantities[product.id] ?? product.qty;
                      const finalPrice = product.dprice
                        ? product.price - (product.price * product.dprice) / 100
                        : product.price;

                      return (
                        <div key={product.id} className="cart-item">
                          {/* Image */}
                          <div className="cart-item-img-wrap d-flex align-items-center justify-content-center">
                            <Link to={`/product/${product.slug}`}>
                              <img
                                src={product.image ? product.image : `${API_BASE_URL}public/noimage.jpg`}
                                alt={product.name}
                              />
                            </Link>
                          </div>

                          {/* Info column */}
                          <div className="cart-item-info">

                            {/* Row 1: name + delete button */}
                            <div className="cart-item-top-row">
                              <Link className="cart-item-name" to={`/product/${product.slug}`}>
                                {product.name}
                              </Link>
                              <button
                                className="cart-delete-btn"
                                onClick={() => removeFromCart(product.id)}
                                title="Remove item"
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            </div>

                            {/* Row 2: short desc (optional) */}
                            {product.short_desc && (
                              <p className="cart-item-desc">{product.short_desc}</p>
                            )}

                            {/* Row 3: price left | qty right */}
                            <div className="cart-item-bottom-row">
                              <div className="cart-item-price-group">
                                {product.dprice && (
                                  <span className="cart-price-original">₹{product.price}</span>
                                )}
                                <span className="cart-price-final">₹{finalPrice}</span>
                                {product.dprice && (
                                  <span className="cart-discount-tag">{product.dprice}% off</span>
                                )}
                              </div>

                              <div className="qty-control">
                                <button
                                  className="qty-control-btn"
                                  type="button"
                                  onClick={() => handleMinus(product.id, product.qty)}
                                >
                                  <i className="fa fa-minus"></i>
                                </button>
                                <input
                                  type="text"
                                  value={qty}
                                  className="input-qty qty-control-input"
                                  readOnly
                                />
                                <button
                                  className="qty-control-btn"
                                  type="button"
                                  onClick={() => handlePlus(product.id, product.qty)}
                                >
                                  <i className="fa fa-plus"></i>
                                </button>
                              </div>
                            </div>

                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="cart-empty-state">
                      <div className="cart-empty-icon">
                        <i className="fa-solid fa-cart-shopping"></i>
                      </div>
                      <div className="cart-empty-title">Your cart is empty</div>
                      <p className="cart-empty-sub">Looks like you haven't added anything yet.</p>
                      <Link to="/category/women-care" className="cart-empty-cta">Start Shopping</Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column — Bill Summary */}
              <div className="col-12 col-xl-4">
                <div className="cart-summary-sticky">
                  <div className="cart-summary-panel">
                    <div className="cart-summary-head">
                      <p className="cart-summary-title">Bill Summary</p>
                    </div>
                    <ul className="cart-bill-list">
                      <li className="cart-bill-row">
                        <span className="cart-bill-label">Total MRP</span>
                        <span className="cart-bill-value">₹ {cartAmount}</span>
                      </li>
                      <li className="cart-bill-row">
                        <span className="cart-bill-label">Delivery charges</span>
                        {deliveryCharges === 0 ? (
                          <span className="cart-bill-free">FREE</span>
                        ) : (
                          <span className="cart-bill-value">₹ {deliveryCharges}</span>
                        )}
                      </li>
                      <li className="cart-bill-row">
                        <span className="cart-bill-label">Cart Value</span>
                        <span className="cart-bill-value">₹ {finalAmount}</span>
                      </li>
                      <li className="cart-bill-row is-total">
                        <span className="cart-bill-label">Amount to pay</span>
                        <span className="cart-bill-value">₹ {finalAmount}</span>
                      </li>
                    </ul>

                    {/* Desktop Checkout Button */}
                      <div className="cart-summary-desktop-checkout mx-3 mb-3">
                        <button
                          className="w-100 d-flex align-items-center justify-content-center dc-book-btn"
                          onClick={handleCheckout}
                          disabled={cartItems.length === 0}
                          style={cartItems.length === 0 ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        >
                          <span>
                            Proceed to Checkout &nbsp;
                            <i className="fa-solid fa-arrow-right"></i>
                          </span>
                        </button>
                        <div className="cart-secure-note">
                          <i className="fa-solid fa-lock"></i>
                          Secure &amp; Encrypted Checkout
                        </div>
                      </div>
                     
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Mobile Sticky Checkout Bar */}
      <div className="mobile-checkout-bar">
        <div className="mobile-checkout-inner">
          <div className="mobile-cart-amount">
            <div className="mobile-cart-amount-label">Total</div>
            <div className="mobile-cart-amount-value">₹ {finalAmount}</div>
          </div>
          <button
            className="mobile-checkout-cta dc-book-btn"
            onClick={handleCheckout}
            disabled={cartItems.length === 0}
            style={cartItems.length === 0 ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            Checkout <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Cart;